import { useCallback, useState } from "react";
import { ROLE_KEYS, type RoleKey, type UserDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";

export function ManageUserCell({
  row,
  isSelf,
  onChanged,
}: {
  row: UserDto;
  isSelf: boolean;
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [pendingState, setPendingState] = useState<{
    user: UserDto;
    activate: boolean;
  } | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const handleRoleChange = useCallback(
    async (role: RoleKey) => {
      try {
        await api.changeUserRole(row.id, role);
        push(
          t("users.roleUpdated"),
          t("users.roleUpdatedDesc", {
            name: row.displayName,
            role: t(`roles.${role}` as MessageKey),
          })
        );
        onChanged();
      } catch (err) {
        if (err instanceof ApiClientError) {
          push(
            err.message,
            err.requestId ? t("common.requestId", { id: err.requestId }) : undefined,
            "danger"
          );
        }
      }
    },
    [onChanged, push, row.displayName, row.id, t]
  );

  const handleToggle = useCallback(async () => {
    if (!pendingState) return;
    setBusy(true);
    try {
      if (pendingState.activate) {
        await api.activateUser(pendingState.user.id, reason);
        push(t("users.activatedToast"), pendingState.user.displayName);
      } else {
        await api.deactivateUser(pendingState.user.id, reason);
        push(t("users.deactivatedToast"), t("users.deactivatedDesc"));
      }
      setPendingState(null);
      setReason("");
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) {
        push(
          err.message,
          err.requestId ? t("common.requestId", { id: err.requestId }) : undefined,
          "danger"
        );
      }
    } finally {
      setBusy(false);
    }
  }, [onChanged, pendingState, push, reason, t]);

  const toggleName = pendingState?.user.displayName ?? "";

  return (
    <div className="flex items-center gap-2">
      <SelectInput
        label={t("users.roleFor", { name: row.displayName })}
        options={ROLE_KEYS.map((key) => ({
          value: key,
          label: t(`roles.${key}` as MessageKey),
        }))}
        value={row.roleKey}
        aria-label={t("users.roleFor", { name: row.displayName })}
        onChange={(e) => void handleRoleChange(e.target.value as RoleKey)}
        className="h-9 min-w-36 text-xs"
      />
      <Button
        size="sm"
        variant={row.active ? "danger" : "secondary"}
        disabled={isSelf}
        onClick={() => setPendingState({ user: row, activate: !row.active })}
      >
        {row.active ? t("users.deactivate") : t("users.activate")}
      </Button>

      <Modal
        open={pendingState !== null}
        onOpenChange={(open) => {
          if (!open) setPendingState(null);
        }}
        title={
          pendingState?.activate
            ? t("users.activateTitle", { name: toggleName })
            : t("users.deactivateTitle", { name: toggleName })
        }
        description={t("users.deactivateDescription")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingState(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant={pendingState?.activate ? "primary" : "danger"}
              onClick={handleToggle}
              disabled={busy || reason.trim().length < 3}
            >
              {pendingState?.activate ? t("users.activate") : t("users.deactivate")}
            </Button>
          </>
        }
      >
        <TextInput
          label={t("common.fields.reason")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          hint={t("users.reasonHint")}
          error={null}
        />
      </Modal>
    </div>
  );
}
